import {
	Box,
	Divider,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Skeleton,
	TextField,
	Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { CountrySelectMenu } from 'ui/CountrySelectMenu';
import { useDispatch, useSelector } from 'react-redux';
import {
	getCurrencies,
	getEUCountries,
	getExchangeRate,
	getGbpExchangeRate,
	getOrderAvailableCorrectionAmount,
	getOrderAvailableCorrectionAmountError,
	getOrderAvailableCorrectionAmountLoader,
	getSystemTime,
} from 'redux/reducers/vatCorrections/selectors';
import {
	getCurrenciesRequest,
	getEUCountriesRequest,
	getExchangeRateRequest,
	getGbpExchangeRateRequest,
	getSystemTimeRequest,
} from 'redux/reducers/vatCorrections/reducer';
import { DateTime } from 'luxon';
import {
	DELIVERY_OPERATORS,
	DELIVERY_OPERATORS_NAMES,
	DeliveryOperator,
	VAT_CORRECTIONS_SCHEME_NAMES,
	VatCorrectionsScheme,
} from 'redux/reducers/vatCorrections/constants';
import { WarningAlert } from 'ui/WarningAlert';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { nanoid } from '@reduxjs/toolkit';
import { InfoDialButton } from 'ui/InfoDialButton';
import { TStep2Props } from './types';
import { Notice } from '../Notice';

const ROWS = [1, 2, 3];

const DAY_OF_PAYMENT = 5;

export const Step2: FC<TStep2Props> = ({
	typeOfReport,
	selectedCountry,
	setSelectedCountry,
	selectedCurrency,
	setSelectedCurrency,
	selectedDate,
	setSelectedDate,
	correctionType,
	setCorrectionType,
	amount,
	setAmount,
	deliveryOperator,
	setDeliveryOperator,
	hasSelectedOrder,
}) => {
	const dispatch = useDispatch();
	const systemTime = useSelector(getSystemTime);
	const currencies = useSelector(getCurrencies);
	const euCountries = useSelector(getEUCountries);
	const exchangeRateEUR = useSelector(getExchangeRate);
	const exchangeRateGBP = useSelector(getGbpExchangeRate);
	const orderAvailableCorrectionAmountError = useSelector(getOrderAvailableCorrectionAmountError);
	const orderAvailableCorrectionAmount = useSelector(getOrderAvailableCorrectionAmount);
	const orderAvailableCorrectionAmountLoading = useSelector(
		getOrderAvailableCorrectionAmountLoader,
	);

	const monthQuarter =
		typeOfReport === VatCorrectionsScheme.IMPORT ||
		typeOfReport === VatCorrectionsScheme.NON_EU_IMPORT
			? 'Month'
			: 'Quarter';

	const [openDate, setOpenDate] = useState(false);
	const handleOpenDate = () => setOpenDate(true);
	const handleCloseDate = () => setOpenDate(false);

	const now = systemTime ? DateTime.fromISO(systemTime.currentSystemTime) : DateTime.now();
	const endDate = monthQuarter === 'Month' ? now.minus({ month: 1 }) : now.minus({ quarter: 1 });
	const startDate = now.minus({ month: 36 });
	const yearArr = Array.from(
		{ length: endDate.year - startDate.year + 1 },
		(_, i) => startDate.plus({ year: i }).year,
	);
	const monthArr = (year: number) => {
		const start = year === startDate.year ? startDate.month : 1;
		const end = year === endDate.year ? endDate.month : 12;
		return Array.from({ length: end - start + 1 }, (_, i) => start + i);
	};

	const isDisabledMonth = (year: number, month: number) => {
		const date = DateTime.fromObject({ year, month });
		return (
			now.minus({ months: 1 }).year === date.year &&
			now.minus({ months: 1 }).month === date.month &&
			now.day < DAY_OF_PAYMENT
		);
	};

	const isDisabledQuarter = (year: number, quarter: number) => {
		const date = DateTime.fromObject({ year, month: quarter * 3 });
		const lastQuarter = now.quarter === 1 ? 4 : now.quarter - 1;
		const lastQuarterYear = now.quarter === 1 ? now.year - 1 : now.year;
		const lastQuarterMonth = lastQuarter * 3;
		const lastQuarterDate = DateTime.fromObject({ year: lastQuarterYear, month: lastQuarterMonth });
		return (
			lastQuarterDate.year === date.year &&
			lastQuarterDate.quarter === date.quarter &&
			lastQuarterDate.year === now.minus({ month: 1 }).year &&
			lastQuarterDate.month === now.minus({ month: 1 }).month &&
			now.day < DAY_OF_PAYMENT
		);
	};

	const quarterArr = (year: number) => {
		const start = year === startDate.year ? startDate.quarter : 1;
		const end = year === endDate.year ? endDate.quarter : 4;
		return Array.from({ length: end - start + 1 }, (_, i) => start + i);
	};

	const [yearMonth, setYearMonth] = useState<number[]>([]);
	useEffect(
		() => {
			if (!selectedDate?.year || !(selectedDate?.month || selectedDate.quarter)) return;
			setYearMonth([selectedDate.year, selectedDate?.month ?? selectedDate.quarter ?? 1]);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);
	useEffect(() => {
		if (hasSelectedOrder && orderAvailableCorrectionAmount) {
			setYearMonth([
				orderAvailableCorrectionAmount.year,
				orderAvailableCorrectionAmount?.period ?? 1,
			]);

			setSelectedDate({
				year: orderAvailableCorrectionAmount.year,
				month:
					orderAvailableCorrectionAmount.periodName === 'month'
						? orderAvailableCorrectionAmount.period
						: undefined,
				quarter:
					orderAvailableCorrectionAmount.periodName === 'quarter'
						? orderAvailableCorrectionAmount.period
						: undefined,
			});
			// setSelectedCountry(orderAvailableCorrectionAmount?. ?? null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderAvailableCorrectionAmount, setSelectedDate]);

	const handleYearMonth = (arr: number[]) => {
		if (arr[arr.length - 1] > 2000) setYearMonth([arr[arr.length - 1]]);
		else {
			setYearMonth([arr[0], arr[arr.length - 1]]);
			setSelectedDate({
				year: arr[0],
				month: monthQuarter === 'Month' ? arr[arr.length - 1] : undefined,
				quarter: monthQuarter === 'Quarter' ? arr[arr.length - 1] : undefined,
			});
			handleCloseDate();
		}
	};

	useEffect(() => {
		dispatch(getSystemTimeRequest());
		dispatch(getCurrenciesRequest());
		dispatch(getEUCountriesRequest());
	}, [dispatch]);

	const handleSelectCountry = (country: string) => {
		setSelectedCountry(country);
	};

	const handleSetSelectedDate = (date: any) => {
		const { year } = date;
		const month = date.month?.toString().padStart(2, '0');
		const { quarter } = date;
		setSelectedDate({ year, month, quarter: undefined });
	};

	const handleSetAmount = (a: number) => {
		if (a < 0) {
			setAmount(0);
			return;
		}
		if (hasSelectedOrder && correctionType === 'Deduct' && !!orderAvailableCorrectionAmount) {
			if (a > orderAvailableCorrectionAmount.availableForCorrection)
				setAmount(orderAvailableCorrectionAmount.availableForCorrection);
			else setAmount(a);
		} else setAmount(a);
	};

	useEffect(() => {
		if (yearMonth[0] && yearMonth[1] && selectedCurrency) {
			if (typeOfReport === VatCorrectionsScheme.NON_EU_IMPORT)
				dispatch(
					getGbpExchangeRateRequest({
						currency: selectedCurrency,
						year: yearMonth[0],
						month: monthQuarter === 'Month' ? yearMonth[1] : yearMonth[1] * 3,
					}),
				);
			else
				dispatch(
					getExchangeRateRequest({
						currency: selectedCurrency,
						year: yearMonth[0],
						month: monthQuarter === 'Month' ? yearMonth[1] : yearMonth[1] * 3,
					}),
				);
		}
	}, [yearMonth, selectedCurrency, dispatch, monthQuarter, typeOfReport]);

	return (
		<>
			<Typography
				sx={{
					fontSize: '1.2rem',
					fontWeight: 'bold',
					color: '#016193',
					mb: '20px',
				}}
			>
				{VAT_CORRECTIONS_SCHEME_NAMES[typeOfReport]}
			</Typography>
			{orderAvailableCorrectionAmountLoading &&
				ROWS.map(() => (
					<Skeleton
						key={nanoid()}
						variant="rectangular"
						height={35}
						sx={{
							width: '100%',
							borderRadius: '12px',
							'& .MuiCardContent-root': { padding: '8px 16px 4px 16px!important' },
							mb: '15px',
						}}
					/>
				))}
			{!orderAvailableCorrectionAmountLoading &&
				(!orderAvailableCorrectionAmountError || !hasSelectedOrder) && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: { xs: 'column', sm: 'row' },
							justifyContent: 'space-between',
							width: '100%',
							gap: '20px',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'left',
								gap: '20px',
								width: '100%',
							}}
						>
							{typeOfReport !== VatCorrectionsScheme.NON_EU_IMPORT && (
								<CountrySelectMenu
									disabled={hasSelectedOrder && !!orderAvailableCorrectionAmount}
									onChange={handleSelectCountry}
									countryList={
										euCountries?.map((item) => {
											return {
												code: item.isoAlpha2,
												label: item.name,
												number: Number(item.isoNumeric),
											};
										}) ?? []
									}
									countryCode={selectedCountry ?? ''}
									required
								/>
							)}
							<FormControl size="small" sx={{ width: '100%' }}>
								<InputLabel required htmlFor="grouped-native-select">
									Sales Period
								</InputLabel>
								<Select
									value={yearMonth ?? ''}
									disabled={hasSelectedOrder && !!orderAvailableCorrectionAmount}
									onChange={(e) => handleYearMonth(e.target.value as number[])}
									open={openDate}
									onClose={handleCloseDate}
									onOpen={handleOpenDate}
									// native
									label="Sales Period"
									multiple
									sx={{
										borderRadius: '12px',
									}}
									size="small"
								>
									<MenuItem disabled value="">
										<em>Year</em>
									</MenuItem>
									{yearArr.map((item) => (
										<MenuItem key={item} value={item}>
											{item}
										</MenuItem>
									))}

									{yearMonth.length > 0 && <Divider />}

									{yearMonth.length > 0 && (
										<MenuItem disabled value="">
											<em>{monthQuarter}</em>
										</MenuItem>
									)}

									{monthQuarter === 'Month' &&
										yearMonth.length > 0 &&
										monthArr(yearMonth[0]).map((item, index) => {
											return (
												<MenuItem
													key={item}
													value={item}
													disabled={isDisabledMonth(yearMonth[0], item)}
													sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
												>
													<Box component="span">
														{DateTime.fromObject({ month: item }).toFormat('MMMM')}
													</Box>
													<Box component="span">
														{isDisabledMonth(yearMonth[0], item)
															? ` available from 3rd ${now.toFormat('MMM')}`
															: ''}
													</Box>
												</MenuItem>
											);
										})}
									{monthQuarter === 'Quarter' &&
										yearMonth.length > 0 &&
										quarterArr(yearMonth[0]).map((item, index) => {
											return (
												<MenuItem
													key={item}
													value={item}
													disabled={isDisabledQuarter(yearMonth[0], item)}
													sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
												>
													<Box component="span"> Quarter {item}</Box>
													<Box component="span">
														{isDisabledQuarter(yearMonth[0], item)
															? `available from 3d ${now.toFormat('MMM')}`
															: ''}
													</Box>
												</MenuItem>
											);
										})}
								</Select>
							</FormControl>
							{correctionType === 'Deduct' && (
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										border: '1px solid #0000003b',
										borderRadius: '12px',
										height: '40px',
										'&:hover': {
											border: '1px solid #000000',
										},

										// justifyContent: 'space-between',
									}}
								>
									<InfoDialButton text="If the negative correction is due to VAT collected by customs, please provide the delivery operator." />
									<FormControl
										size="small"
										// variant="filled"
										sx={{
											width: '100%',
											border: 0,
											// backgroundColor: 'transparent',
											// '&.MuiInputBase-root': {
											// 	border: '0 solid #aa6193!important',
											// 	backgroundColor: 'transparent!important',
											// },
											'& label': {
												zIndex: '1000!important',
												padding: '0 5px!important',
												backgroundColor: 'white!important',
											},
										}}
									>
										<InputLabel>Delivery Operator</InputLabel>
										{/* // !  If the negative correction is due to VAT collected by customs, please provide the delivery operator. */}
										<Select
											sx={{
												borderRadius: '12px',
												'& fieldset': {
													border: '0!important',
												},
												'& label': {
													zIndex: '1000!important',
													padding: '0 5px!important',
													backgroundColor: 'white!important',
												},
											}}
											label="Delivery Operator"
											value={deliveryOperator ?? ''}
											onChange={(e) => setDeliveryOperator(e.target.value as DeliveryOperator)}
										>
											{DELIVERY_OPERATORS.map((operator) => (
												<MenuItem key={operator} value={operator}>
													{
														DELIVERY_OPERATORS_NAMES[
															operator as keyof typeof DELIVERY_OPERATORS_NAMES
														]
													}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>
							)}
						</Box>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'left',
								gap: '20px',
								width: '100%',
							}}
						>
							<FormControl size="small" sx={{ width: '100%' }}>
								<InputLabel required>Correction Type</InputLabel>
								<Select
									sx={{
										borderRadius: '12px',
									}}
									label="Correction Type"
									value={correctionType ?? ''}
									onChange={(e) => setCorrectionType(e.target.value as 'Add' | 'Deduct')}
									disabled
								>
									<MenuItem value="Add">Add</MenuItem>
									<MenuItem value="Deduct">Deduct</MenuItem>
								</Select>
							</FormControl>
							<Box>
								<Box>
									<TextField
										required
										sx={{
											width: 'calc(100% - 130px)',
											'& .MuiInputBase-root': {
												borderRadius: '12px',
											},
										}}
										label="VAT Correction Amount"
										variant="outlined"
										type="number"
										size="small"
										value={amount ?? ''}
										onChange={(e) => handleSetAmount(Number(e.target.value))}
									/>
									<FormControl size="small" sx={{ ml: '10px', minWidth: '120px' }}>
										<InputLabel required>Currency</InputLabel>
										<Select
											disabled={hasSelectedOrder && !!orderAvailableCorrectionAmount}
											sx={{
												borderRadius: '12px',
											}}
											label="Currency"
											value={selectedCurrency ?? ''}
											onChange={(e) => setSelectedCurrency(e.target.value)}
										>
											{currencies?.map((currency) => (
												<MenuItem key={currency.name} value={currency.name}>
													{currency.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>
								<Box
									sx={{
										fontSize: '.8rem',
										color: '#016193',
										mt: '5px',
										ml: '10px',
										width: '340px',
									}}
								>
									{hasSelectedOrder &&
										!!orderAvailableCorrectionAmount &&
										correctionType === 'Deduct' && (
											<Box>
												<Box sx={{ color: 'primary.main' }}>
													Maximum correction amount:{' '}
													<AmountAndCurrency
														amount={orderAvailableCorrectionAmount.availableForCorrection}
														currency={selectedCurrency ?? 'EUR'}
														bold
													/>
												</Box>
											</Box>
										)}
									{(exchangeRateEUR || exchangeRateGBP) &&
										yearMonth[0] &&
										yearMonth[1] &&
										selectedCurrency &&
										((typeOfReport === VatCorrectionsScheme.NON_EU_IMPORT &&
											selectedCurrency !== 'GBP') ||
											(typeOfReport !== VatCorrectionsScheme.NON_EU_IMPORT &&
												selectedCurrency !== 'EUR')) && (
											<Box>
												{typeOfReport === VatCorrectionsScheme.NON_EU_IMPORT && !exchangeRateGBP ? (
													<Box sx={{ color: 'error.main' }}>
														No exchange rate for selected currency and reporting period! Please
														select GBP as the currency for the vat correction submission
													</Box>
												) : (typeOfReport === VatCorrectionsScheme.IMPORT ||
														typeOfReport === VatCorrectionsScheme.NON_UNION ||
														typeOfReport === VatCorrectionsScheme.UNION) &&
												  !exchangeRateEUR ? (
													<Box sx={{ color: 'error.main' }}>
														No exchange rate for selected currency and reporting period! Please
														select EUR as the currency for the vat correction submission
													</Box>
												) : (
													<>
														Exchange rate 1 {selectedCurrency} ={' '}
														{typeOfReport === VatCorrectionsScheme.NON_EU_IMPORT ? (
															<>{exchangeRateGBP} GBP </>
														) : (
															<>{exchangeRateEUR} EUR</>
														)}{' '}
														will be applied
													</>
												)}
											</Box>
										)}
								</Box>
							</Box>
						</Box>
					</Box>
				)}
			{hasSelectedOrder &&
				!orderAvailableCorrectionAmountLoading &&
				orderAvailableCorrectionAmountError && (
					<Box
						sx={{
							color: 'error.main',
							fontSize: '1rem',
							fontWeight: 'bold',
							mt: '20px',
						}}
					>
						<WarningAlert>{orderAvailableCorrectionAmountError}</WarningAlert>
					</Box>
				)}
			{!orderAvailableCorrectionAmountLoading &&
				!!typeOfReport &&
				!orderAvailableCorrectionAmountError && <Notice typeOfReport={typeOfReport} />}
		</>
	);
};
